import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabaseClient';

// Création du contexte d'authentification
const AuthContext = createContext(null);

// Hook personnalisé pour utiliser le contexte d'authentification
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé à l\'intérieur d\'un AuthProvider');
  }
  return context;
};

// Fournisseur du contexte d'authentification
export const AuthProvider = ({ children }) => {
  // État utilisateur et chargement
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialiser l'authentification
  useEffect(() => {
    let mounted = true;
    //console.log('AuthProvider: Initialisation');

    // Fonction pour récupérer la session et l'utilisateur actuels
    const getInitialSession = async () => {
      try {
        //console.log('AuthProvider: Récupération de la session initiale');
        const { data } = await supabase.auth.getSession();
        
        // Mettre à jour l'état si le composant est toujours monté
        if (mounted) {
          if (data && data.session) {
            //console.log('AuthProvider: Session trouvée', data.session);
            setSession(data.session);
            setUser(data.session.user);
          } else {
            console.log('AuthProvider: Pas de session trouvée');
            setSession(null);
            setUser(null);
          }
          
          // Important: Toujours mettre fin au chargement
          setLoading(false);
        }
      } catch (error) {
        console.error('AuthProvider: Erreur lors de la récupération de la session', error);
        // Important: Toujours mettre fin au chargement même en cas d'erreur
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Récupérer la session initiale
    getInitialSession();

    // Variable pour suivre si c'est la première initialisation
    let isInitializing = true;
    
    // Configurer l'écouteur pour les changements d'authentification
    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      //console.log('AuthProvider: Événement d\'authentification', event);
      
      if (mounted) {
        // Mettre à jour la session
        setSession(newSession);
        
        // Mettre à jour l'utilisateur
        setUser(newSession?.user || null);
        
        // Afficher un message selon l'événement, mais pas pendant l'initialisation
        if (!isInitializing) {
          // Afficher le toast uniquement pour les événements explicites, pas pour les actualisations de session
          if (event === 'SIGNED_IN' && window.location.pathname.includes('/admin/login')) {
            toast.success('Connexion réussie!');
          } else if (event === 'SIGNED_OUT') {
            toast.success('Déconnexion réussie!');
          }
        }
        
        // Mettre fin au chargement
        setLoading(false);
        
        // Après le premier événement, ce n'est plus l'initialisation
        isInitializing = false;
      }
    });

    // Nettoyage lors du démontage
    return () => {
      mounted = false;
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // Fonction de connexion
  const signIn = async (email, password) => {
    try {
      //console.log('AuthProvider: Tentative de connexion', email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('AuthProvider: Erreur de connexion', error);
        throw error;
      }
      
      //console.log('AuthProvider: Connexion réussie', data);
      // Afficher le toast de connexion réussie ici plutôt que dans l'écouteur
      toast.success('Connexion réussie!');
      return { success: true, data };
    } catch (error) {
      console.error('AuthProvider: Erreur de connexion', error);
      return { 
        success: false, 
        error: error.message || 'Échec de la connexion. Vérifiez vos identifiants.'
      };
    }
  };

  // Fonction de déconnexion
  const signOut = async () => {
    try {
      //console.log('AuthProvider: Tentative de déconnexion');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('AuthProvider: Erreur de déconnexion', error);
        throw error;
      }
      
      //console.log('AuthProvider: Déconnexion réussie');
      return { success: true };
    } catch (error) {
      console.error('AuthProvider: Erreur de déconnexion', error);
      return { 
        success: false, 
        error: error.message || 'Échec de la déconnexion.'
      };
    }
  };

  // Fonction de réinitialisation du mot de passe
  const resetPassword = async (email) => {
    try {
      //console.log('AuthProvider: Tentative de réinitialisation du mot de passe', email);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/admin/reset-password`,
      });
      
      if (error) {
        console.error('AuthProvider: Erreur de réinitialisation', error);
        throw error;
      }
      
      //console.log('AuthProvider: Réinitialisation envoyée');
      return { 
        success: true, 
        message: 'Instructions de réinitialisation envoyées à votre email.'
      };
    } catch (error) {
      console.error('AuthProvider: Erreur de réinitialisation', error);
      return { 
        success: false, 
        error: error.message || 'Échec de l\'envoi des instructions de réinitialisation.'
      };
    }
  };

  // Fonction de mise à jour du mot de passe
  const updatePassword = async (newPassword) => {
    try {
      //console.log('AuthProvider: Tentative de mise à jour du mot de passe');
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      
      if (error) {
        console.error('AuthProvider: Erreur de mise à jour du mot de passe', error);
        throw error;
      }
      
      //console.log('AuthProvider: Mot de passe mis à jour');
      return { 
        success: true, 
        message: 'Mot de passe mis à jour avec succès.'
      };
    } catch (error) {
      //console.error('AuthProvider: Erreur de mise à jour du mot de passe', error);
      return { 
        success: false, 
        error: error.message || 'Échec de la mise à jour du mot de passe.'
      };
    }
  };

  // Valeur du contexte
  const value = {
    session,
    user,
    loading,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};
